/*
  Состав должников по лицевому счёту - §3.3.5, §6.4.2, §6.9.

  До 09.09.2026 карточка отвечала на вопрос «сколько» и молчала о том, «с
  кого»: ни фамилии, ни идентификаторов на экране не было вовсе, хотя именно
  они уходят в заявление. Замечание владельца.

  Два правила подачи, оба из права, а не из вкуса:

  * **Пропуск называется словами.** Отсутствие ИНН, СНИЛС и паспорта - штатная
    ситуация, суд запросит сведения сам (п. 3 ч. 2 ст. 124 ГПК РФ). Но видеть это
    оператор обязан до подачи, поэтому пустая клетка заменена на «не указан», а
    у человека без единого идентификатора стоит отдельная пометка.
  * **Ответственность и доля стоят рядом с именем.** При долевой
    ответственности по одному счёту подаётся несколько заявлений и платится
    несколько пошлин - и это первое, что нужно понять, открыв счёт.
*/

import { useState } from 'react'

import { LIABILITY_TITLES, PERSON_ROLE_TITLES, date, percent } from '../api/format.js'
import { Empty, Tag } from './Common.jsx'
import DebtorForm from './DebtorForm.jsx'
import PageBar from './PageBar.jsx'
import usePaging from './usePaging.js'

/*
  Значение или честная пометка о пропуске: пустая клетка его скрывает.

  Форма пометки приходит вместе с полем, а не собирается одна на все: «дата
  рождения не указан» - это не мелочь, кабинет читает юрист.
*/
function Value({ missing, children }) {
  if (children === null || children === undefined || children === '') {
    return <span className="muted">{missing}</span>
  }
  return <span>{children}</span>
}

/** Чем отвечает человек: солидарно или своей долей (§6.9). */
function liabilityOf(debtor) {
  const title = LIABILITY_TITLES[debtor.liability]
  if (debtor.liability !== 'shared' || debtor.share === null) return title
  return `${title}, ${percent(debtor.share)}`
}

function Person({ debtor, onEdit, onEditLink, onRemoveLink }) {
  const rows = [
    ['Дата рождения', debtor.birth_date ? date(debtor.birth_date) : null, 'не указана'],
    // Место рождения стоит сразу за датой (30.09.2026): в заявлении они идут
    // рядом и указываются, если известны взыскателю.
    ['Место рождения', debtor.birth_place, 'не указано'],
    ['ИНН', debtor.inn, 'не указан'],
    ['СНИЛС', debtor.snils, 'не указан'],
    ['Паспорт', debtor.passport, 'не указан'],
    ['Телефон', debtor.phone, 'не указан'],
    ['Почта', debtor.email, 'не указана'],
  ]
  if (debtor.valid_from || debtor.valid_to) {
    rows.push([
      'Отвечает за период',
      `${debtor.valid_from ? date(debtor.valid_from) : 'с начала'} - ${
        debtor.valid_to ? date(debtor.valid_to) : 'по настоящее время'
      }`,
      'не указан',
    ])
  }

  return (
    <div className="card">
      <div className="section__title">{debtor.full_name}</div>
      <div className="row">
        <Tag kind="muted">{PERSON_ROLE_TITLES[debtor.role]}</Tag>
        <Tag kind="muted">{liabilityOf(debtor)}</Tag>
        {debtor.is_deceased ? (
          <Tag kind="warn">
            умер{debtor.deceased_at ? ` ${date(debtor.deceased_at)}` : ''}
          </Tag>
        ) : null}
      </div>

      <table className="table table--pairs">
        <tbody>
          {rows.map(([label, value, missing]) => (
            <tr key={label}>
              <td className="muted">{label}</td>
              <td>
                <Value missing={missing}>{value}</Value>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {debtor.has_identifier ? null : (
        <div className="muted">
          Ни одного идентификатора нет - заявление подаётся с ходатайством, и суд запросит их сам
          (пункт 3 части 2 статьи 124 ГПК РФ).
        </div>
      )}

      {/* Кнопки нет вовсе у того, кому правка не положена: предлагать действие,
          в котором сервер заведомо откажет, значит обещать невозможное. Право то
          же, что на сборку пакета: идентификаторы уходят в заявление, и правка
          их - процессуальное действие (17.09.2026). */}
      {onEdit ? (
        <div className="row row--tight">
          <button className="button button--quiet" type="button" onClick={onEdit}>
            Поправить сведения
          </button>
          {/* Связь со счётом - роль, ответственность, доля, срок - правится отдельно от
              сведений о самом человеке (20.10.2026). */}
          {onEditLink ? (
            <button className="button button--quiet" type="button" onClick={onEditLink}>
              Изменить связь
            </button>
          ) : null}
          {onRemoveLink ? (
            <button className="button button--quiet" type="button" onClick={onRemoveLink}>
              Убрать из состава
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

/*
  Правка вызывается с карточки счёта, а не из отдельного раздела: человек
  смотрит, на кого подаёт, видит пропуск и заполняет его здесь же. Открытая
  форма закрывается сохранением, и карточка перечитывается целиком - от
  идентификаторов зависит текст заявления, а не только эта таблица.
*/
export default function AccountDebtors({
  debtors,
  canEdit = false,
  onSaved,
  onAdd,
  onEditLink,
  onRemoveLink,
}) {
  const [editing, setEditing] = useState(null)
  // Форма правки ищет человека во ВСЁМ составе, а не на странице: открытая карточка
  // остаётся открытой, что бы ни нажали в панели страниц. С 21.10.2026 состав разбит на
  // страницы общей панелью (в доме на сто собственников карточек было бы сто).
  const opened = debtors.find((debtor) => debtor.person_id === editing)
  const { rows: pageDebtors, bar } = usePaging(debtors)

  return (
    <section className="section">
      <div className="row row--spread">
        <h2 className="section__title">Должники</h2>
        {onAdd ? (
          <button className="button" type="button" onClick={onAdd}>
            Добавить должника
          </button>
        ) : null}
      </div>
      {debtors.length === 0 ? (
        <Empty title="По счёту не указан ни один субъект права">
          Заявление подавать не на кого: состав собственников или нанимателей приходит из вашей
          выгрузки или вносится кнопкой «Добавить должника».
        </Empty>
      ) : (
        <div className="grid">
          {pageDebtors.map((debtor) => (
            <Person
              key={debtor.person_id}
              debtor={debtor}
              onEdit={canEdit ? () => setEditing(debtor.person_id) : undefined}
              onEditLink={onEditLink ? () => onEditLink(debtor) : undefined}
              onRemoveLink={onRemoveLink ? () => onRemoveLink(debtor) : undefined}
            />
          ))}
        </div>
      )}
      <PageBar {...bar} what="должников" />

      {/*
        Форма живёт здесь, а не внутри карточки человека, и создаётся заново на
        каждое открытие: иначе при следующем открытии в ней висела бы ошибка от
        прошлой попытки и наполовину набранные чужие значения.
      */}
      {opened ? (
        <DebtorForm
          debtor={opened}
          onCancel={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            onSaved()
          }}
        />
      ) : null}
    </section>
  )
}
